import {FormEvent, useId, useState} from "react";
import {Alert, Badge, Button, Form, Modal, Table} from "react-bootstrap";
import {UsersApi} from "../../api/UsersApi";
import {AccountState, EarlyAccessUserClaim, ExternalUserClaim, SiteAdministratorClaim, UserViewModel} from "../../api/models";
import {useAuth} from "../../auth/AuthContext";
import {useAsync} from "../../components/useAsync";
import {Avatar} from "../../components/Avatar";
import {ErrorAlert, fieldError} from "../../components/ErrorAlert";
import {LoadingSpinner} from "../../components/LoadingSpinner";
import {PageHeader} from "../../components/PageHeader";
import {ConfirmModal} from "../../components/ConfirmModal";
import {SubmitButton} from "../../components/SubmitButton";
import {emailProblem, formatDate, formatDateTime, usernameProblem} from "../../components/format";
import {SortHeader, useSort} from "../../components/sortable";
import {Field, FieldControl} from "../../components/Field";
import {UserNotes} from "./UserNotes";
import {usePasswordConfirmation} from "../../components/PasswordConfirmation";

const hasClaim = (user: UserViewModel, claim: string) => !!user.claims?.includes(claim);
const isAdmin = (user: UserViewModel) => hasClaim(user, SiteAdministratorClaim);
const isCustomer = (user: UserViewModel) => hasClaim(user, ExternalUserClaim);
const isEarlyAccessUser = (user: UserViewModel) => hasClaim(user, EarlyAccessUserClaim);

function withClaim(claims: string[], claim: string, on: boolean): string[] {
    const others = (claims ?? []).filter(x => x !== claim);
    return on ? [...others, claim] : others;
}

// customers: accounts with the External User role (made at checkout, or by hand). staff: the company's own, the rest.
export type UserAudience = 'customers' | 'staff';

export function CustomersPage() {
    return <UserList audience="customers"/>;
}

type Confirm = { kind: 'reset' | 'mfa' | 'delete', user: UserViewModel };

const hasMfa = (user: UserViewModel) => !!user.mfa?.totpEnabled || !!user.mfa?.emailEnabled;

export function UsersPage() {
    return <UserList audience="staff"/>;
}

function UserList({audience}: { audience: UserAudience }) {
    const customers = audience === 'customers';
    const {user: me} = useAuth();
    const {data: users, error, loading, reload} = useAsync(
        () => UsersApi.getAll().then(x => x.filter(user => isCustomer(user) === customers).sort((l, r) => l.username.localeCompare(r.username))), [customers]);
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState<UserViewModel>();
    const [confirm, setConfirm] = useState<Confirm>();
    const [busy, setBusy] = useState(false);
    const [actionError, setActionError] = useState<unknown>();
    const [notice, setNotice] = useState<string>();
    const {sorted, sort, toggle} = useSort(customers ? 'admin-customers:sort' : 'admin-users:sort', users, {
        username: x => x.username,
        displayName: x => x.displayName,
        email: x => x.emailAddress,
        status: x => x.status,
        created: x => x.createdOn,
        lastSignIn: x => x.lastSignInOn
    });

    // The API refuses to delete the signed-in user or 'admin'.
    const canDelete = (user: UserViewModel) =>
        user.username.toLowerCase() !== 'admin' && user.username.toLowerCase() !== me?.username.toLowerCase();

    const runConfirmed = async () => {
        if (!confirm) {
            return;
        }
        setBusy(true);
        setActionError(undefined);
        try {
            if (confirm.kind === 'reset') {
                await UsersApi.resetPassword(confirm.user.username);
                setNotice(`A new password has been emailed to ${confirm.user.emailAddress}.`);
            } else if (confirm.kind === 'mfa') {
                await UsersApi.resetMfa(confirm.user.username);
                setNotice(`Two-factor authentication is now off for ${confirm.user.username}.`);
            } else {
                await UsersApi.remove(confirm.user.username);
                setNotice(`${confirm.user.username} has been deleted.`);
            }
            reload();
        } catch (err) {
            setActionError(err);
        } finally {
            setBusy(false);
            setConfirm(undefined);
        }
    };

    return (
        <>
            <PageHeader title={customers ? 'Customers' : 'Users'}>
                <Button onClick={() => setCreating(true)}><i className="bi bi-person-plus me-1"></i>{customers ? 'New customer' : 'New user'}</Button>
            </PageHeader>
            {notice && <Alert variant="success" dismissible onClose={() => setNotice(undefined)}>{notice}</Alert>}
            <ErrorAlert error={error}/>
            <ErrorAlert error={actionError} onClose={() => setActionError(undefined)}/>
            {loading && !users && <LoadingSpinner/>}
            {users && users.length === 0 && (
                <p className="text-body-secondary">{customers
                    ? 'No customers yet. Accounts made at checkout are customers, and others become one with the Customer switch.'
                    : 'No users.'}</p>
            )}
            {users && users.length > 0 && (
                <Table responsive hover>
                    <thead>
                    <tr>
                        <SortHeader column="username" sort={sort} onSort={toggle}>Username</SortHeader>
                        <SortHeader column="displayName" sort={sort} onSort={toggle}>Display name</SortHeader>
                        <SortHeader column="email" sort={sort} onSort={toggle}>Email</SortHeader>
                        <SortHeader column="status" sort={sort} onSort={toggle}>Status</SortHeader>
                        <SortHeader column="created" sort={sort} onSort={toggle}>Created</SortHeader>
                        <SortHeader column="lastSignIn" sort={sort} onSort={toggle}>Last sign-in</SortHeader>
                        <th></th>
                    </tr>
                    </thead>
                    <tbody>
                    {sorted.map(user => (
                        <tr key={user.username}>
                            <td>{user.username} {isAdmin(user) && <Badge bg="primary" className="ms-1">Admin</Badge>}
                                {isEarlyAccessUser(user) && <Badge bg="warning" text="dark" className="ms-1" title="Early Access User">EAP</Badge>}
                                {hasMfa(user) && <Badge bg="info" text="dark" className="ms-1" title="Two-factor authentication is on">2FA</Badge>}</td>
                            <td><Avatar url={user.avatarUrl} size="sm" className="me-2 align-middle"/>{user.displayName}</td>
                            <td>
                                {user.emailAddress}
                                {user.emailUndeliverable && <Badge bg="danger" className="ms-1" title="Emails to this address bounce">Bouncing</Badge>}
                            </td>
                            <td>{user.status === AccountState.Enabled ? <Badge bg="success">Enabled</Badge> : <Badge bg="secondary">Disabled</Badge>}</td>
                            <td>{formatDate(user.createdOn)}</td>
                            <td className="text-nowrap">
                                {user.lastSignInOn
                                    ? formatDateTime(user.lastSignInOn)
                                    : <Badge bg="warning" text="dark" title="Their first password may not have reached them">Never</Badge>}
                            </td>
                            <td className="text-end text-nowrap">
                                <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => setEditing(user)}>Edit</Button>
                                <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => setConfirm({ kind: 'reset', user })}>Reset password</Button>
                                {hasMfa(user) && <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => setConfirm({ kind: 'mfa', user })}>Reset 2FA</Button>}
                                <Button size="sm" variant="outline-danger" disabled={!canDelete(user)} onClick={() => setConfirm({ kind: 'delete', user })}>Delete</Button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </Table>
            )}
            <CreateUserModal show={creating} customer={customers} onHide={() => setCreating(false)} onCreated={user => {
                setCreating(false);
                setNotice(`${user.username} has been created. Their password has been emailed to ${user.emailAddress}.`);
                reload();
            }}/>
            {editing && <EditUserModal user={editing} isSelf={editing.username.toLowerCase() === me?.username.toLowerCase()}
                                       onHide={() => setEditing(undefined)} onSaved={saved => {
                                           setEditing(undefined);
                                           // Moved to the other list: say where it went, since it's no longer in this one.
                                           if (isCustomer(saved) !== customers) {
                                               setNotice(`${saved.username} is now listed under ${customers ? 'Users' : 'Customers'}.`);
                                           }
                                           reload();
                                       }}
                                       onChanged={updated => { setEditing(updated); reload(); }}/>}
            <ConfirmModal show={confirm?.kind === 'reset'} title="Reset password?" confirmLabel="Reset password" variant="warning" busy={busy}
                          onConfirm={runConfirmed} onCancel={() => setConfirm(undefined)}>
                A new password will be generated and emailed to <strong>{confirm?.user.emailAddress}</strong>. Their current
                password stops working, and a disabled account is enabled again.
            </ConfirmModal>
            <ConfirmModal show={confirm?.kind === 'mfa'} title="Turn off two-factor authentication?" confirmLabel="Turn off" variant="warning" busy={busy}
                          onConfirm={runConfirmed} onCancel={() => setConfirm(undefined)}>
                <strong>{confirm?.user.username}</strong> will be able to sign in with just their password. Their authenticator
                app, email codes and recovery codes are all removed, and they're emailed at <strong>{confirm?.user.emailAddress}</strong>.
                Only do this once you're sure it's really them asking.
            </ConfirmModal>
            <ConfirmModal show={confirm?.kind === 'delete'} title="Delete user?" confirmLabel="Delete" busy={busy}
                          onConfirm={runConfirmed} onCancel={() => setConfirm(undefined)}>
                <strong>{confirm?.user.username}</strong> will be deleted. This can't be undone. Licenses they own keep working,
                but no longer appear on anyone's account.
            </ConfirmModal>
        </>
    );
}

// customer: an External User, who can't be an administrator.
function CreateUserModal({show, customer, onHide, onCreated}: { show: boolean, customer: boolean, onHide(): void, onCreated(user: UserViewModel): void }) {
    const titleId = useId();
    // Making an administrator may need the password again.
    const confirmPassword = usePasswordConfirmation();
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [admin, setAdmin] = useState(false);
    const [earlyAccess, setEarlyAccess] = useState(false);
    const [validated, setValidated] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();
    const problem = emailProblem(email);
    const usernameError = usernameProblem(username);

    const reset = () => {
        setUsername('');
        setEmail('');
        setDisplayName('');
        setAdmin(false);
        setEarlyAccess(false);
        setValidated(false);
        setError(undefined);
    };

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setValidated(true);
        if (problem || usernameError || !displayName.trim()) {
            return;
        }
        setBusy(true);
        setError(undefined);
        try {
            const created = await confirmPassword(currentPassword => UsersApi.create({
                username: username.trim(),
                emailAddress: email.trim(),
                displayName: displayName.trim(),
                claims: [
                    ...(customer ? [ExternalUserClaim] : admin ? [SiteAdministratorClaim] : []),
                    ...(earlyAccess ? [EarlyAccessUserClaim] : [])
                ],
                timezone: '',
                language: 'en-US'
            }, currentPassword));
            if (created) {
                reset();
                onCreated(created.value);
            }
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal aria-labelledby={titleId} show={show} onHide={() => { reset(); onHide(); }} centered>
            <Form noValidate onSubmit={submit}>
                <Modal.Header closeButton><Modal.Title id={titleId}>{customer ? 'New customer' : 'New user'}</Modal.Title></Modal.Header>
                <Modal.Body>
                    <ErrorAlert error={error} shownFields={['username', 'emailAddress', 'displayName']}/>
                    <Field className="mb-3" controlId="new-user-username" label="Username"
                           help="Letters, digits, dashes and underscores only." error={(validated && usernameError) || fieldError(error, 'username')}>
                        <FieldControl autoFocus value={username} onChange={e => setUsername(e.target.value)}/>
                    </Field>
                    <Field className="mb-3" controlId="new-user-email" label="Email address" error={(validated && problem) || fieldError(error, 'emailAddress')}>
                        <FieldControl type="email" value={email} onChange={e => setEmail(e.target.value)}/>
                    </Field>
                    <Field className="mb-3" controlId="new-user-display-name" label="Display name" error={(validated && !displayName.trim() && 'Enter a display name.') || fieldError(error, 'displayName')}>
                        <FieldControl value={displayName} onChange={e => setDisplayName(e.target.value)}/>
                    </Field>
                    {!customer && <Form.Check id="new-user-admin" label="Site administrator" checked={admin} onChange={e => setAdmin(e.target.checked)}/>}
                    <Form.Check id="new-user-early-access" label="Early Access User" checked={earlyAccess} onChange={e => setEarlyAccess(e.target.checked)}/>
                    <Form.Text>A password is generated and emailed to the new {customer ? 'customer' : 'user'}.</Form.Text>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { reset(); onHide(); }} disabled={busy}>Cancel</Button>
                    <SubmitButton busy={busy}>{customer ? 'Create customer' : 'Create user'}</SubmitButton>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}

// onChanged: something changed at once (not on Save), with the user as they are now.
function EditUserModal({user, isSelf, onHide, onSaved, onChanged}: {
    user: UserViewModel, isSelf: boolean, onHide(): void, onSaved(user: UserViewModel): void, onChanged(user: UserViewModel): void
}) {
    const titleId = useId();
    // Making them an administrator may need the password again.
    const confirmPassword = usePasswordConfirmation();
    const [displayName, setDisplayName] = useState(user.displayName);
    const [enabled, setEnabled] = useState(user.status === AccountState.Enabled);
    const [admin, setAdmin] = useState(isAdmin(user));
    const [customer, setCustomer] = useState(isCustomer(user));
    const [earlyAccess, setEarlyAccess] = useState(isEarlyAccessUser(user));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setError(undefined);
        try {
            // The API replaces every field: keep the ones this form doesn't edit, and any other claims.
            let claims = withClaim(user.claims, SiteAdministratorClaim, admin && !customer);
            claims = withClaim(claims, ExternalUserClaim, customer);
            claims = withClaim(claims, EarlyAccessUserClaim, earlyAccess);
            const saved = await confirmPassword(currentPassword => UsersApi.update(user.username, {
                displayName: displayName.trim(),
                timezone: user.timezone,
                language: user.language,
                defaultProjectId: user.defaultProjectId,
                status: enabled ? AccountState.Enabled : AccountState.Disabled,
                claims
            }, currentPassword));
            if (saved) {
                onSaved(saved.value);
            }
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    // At once rather than on Save, so the rest of the form can still be cancelled.
    const removeAvatar = async () => {
        setBusy(true);
        setError(undefined);
        try {
            onChanged(await UsersApi.removeAvatar(user.username));
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    // The mailbox works again: at once, like removing the picture.
    const clearUndeliverable = async () => {
        setBusy(true);
        setError(undefined);
        try {
            await UsersApi.clearEmailUndeliverable(user.username);
            onChanged({ ...user, emailUndeliverable: null });
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal aria-labelledby={titleId} show onHide={onHide} centered>
            <Form onSubmit={submit}>
                <Modal.Header closeButton><Modal.Title id={titleId}>Edit {user.username}</Modal.Title></Modal.Header>
                <Modal.Body>
                    <ErrorAlert error={error}/>
                    <div className="d-flex align-items-center gap-3 mb-3">
                        <Avatar url={user.avatarUrl} size="lg"/>
                        {user.avatarUrl
                            ? <Button variant="outline-danger" size="sm" disabled={busy} onClick={removeAvatar}>Remove picture</Button>
                            : <span className="text-body-secondary small">No picture. Users set their own from their profile.</span>}
                    </div>
                    <Field className="mb-3" controlId="edit-user-email" label="Email address" help="Users change their own address from their profile.">
                        <FieldControl value={user.emailAddress} readOnly plaintext/>
                    </Field>
                    {user.emailUndeliverable && (
                        <Alert variant="warning" className="small">
                            Emails to this address have bounced since {formatDate(user.emailUndeliverable.on)}, so they're not being
                            sent. The user is asked to change it when they sign in. If the mailbox works again, clear this so they're
                            sent again.
                            <div className="mt-2">
                                <Button variant="outline-secondary" size="sm" disabled={busy} onClick={clearUndeliverable}>Clear: the mailbox works again</Button>
                            </div>
                        </Alert>
                    )}
                    <Form.Group className="mb-3" controlId="edit-user-display-name">
                        <Form.Label>Display name</Form.Label>
                        <Form.Control required value={displayName} onChange={e => setDisplayName(e.target.value)}/>
                    </Form.Group>
                    {/* Locking yourself out of the admin area from here would need another admin to undo. */}
                    <Form.Check type="switch" id="edit-user-enabled" className="mb-2" label="Account enabled" checked={enabled}
                                disabled={isSelf} aria-describedby={isSelf ? 'edit-user-self' : undefined} onChange={e => setEnabled(e.target.checked)}/>
                    {/* A customer is never an administrator: the API refuses both. */}
                    <Form.Check type="switch" id="edit-user-customer" className="mb-2" label="Customer (External User)" checked={customer}
                                disabled={isSelf} aria-describedby={isSelf ? 'edit-user-self' : 'edit-user-customer-help'}
                                onChange={e => setCustomer(e.target.checked)}/>
                    <Form.Check type="switch" id="edit-user-admin" className="mb-2" label="Site administrator" checked={admin && !customer}
                                disabled={isSelf || customer} aria-describedby={isSelf ? 'edit-user-self' : undefined} onChange={e => setAdmin(e.target.checked)}/>
                    <Form.Check type="switch" id="edit-user-early-access" label="Early Access User" checked={earlyAccess}
                                aria-describedby="edit-user-early-access-help" onChange={e => setEarlyAccess(e.target.checked)}/>
                    <Form.Text id="edit-user-early-access-help" className="d-block">
                        Can be given Early Access licenses. Turning this off stops theirs from activating and refreshing.
                    </Form.Text>
                    {!isSelf && <Form.Text id="edit-user-customer-help" className="d-block">Customers are listed under Customers, and can't be site administrators.</Form.Text>}
                    {isSelf && <Form.Text id="edit-user-self" className="d-block">You can't disable your own account, remove your own administrator access or make yourself a customer.</Form.Text>}
                    {/* Saved as they're added, like removing the picture, not on Save. */}
                    <h3 className="h6 mt-4">Notes</h3>
                    <UserNotes username={user.username}/>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={busy}>Cancel</Button>
                    <SubmitButton busy={busy}>Save</SubmitButton>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}
